/**
 * Page "Mes réclamations" : historique avec statut, conversation et réponses scolarité.
 */
import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { PortailLayout } from '../PortailLayout';
import {
  portailApi, ReclamationItem, FenetreReclamation, MessageReclamationItem,
} from '../services/portailApi';

const STATUT_INFO: Record<string, { label: string; cls: string; icon: string }> = {
  deposee: { label: 'Déposée', cls: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300', icon: '⏳' },
  en_examen: { label: 'En cours d\'examen', cls: 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300', icon: '🔍' },
  acceptee: { label: 'Acceptée', cls: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300', icon: '✓' },
  rejetee: { label: 'Rejetée', cls: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300', icon: '✗' },
  cloturee: { label: 'Clôturée', cls: 'bg-gray-200 text-gray-800 dark:bg-gray-700 dark:text-gray-200', icon: '🔒' },
};

const TYPE_LABEL: Record<string, string> = {
  devoir: 'Note de devoir',
  examen: 'Note d\'examen',
  rattrapage: 'Note de rattrapage',
  autre: 'Autre',
};

function fmtDate(s: string): string {
  return new Date(s).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' });
}

function fmtTime(s: string): string {
  return new Date(s).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
}

const Fenetre: React.FC<{ fenetre: FenetreReclamation }> = ({ fenetre }) => {
  if (fenetre.ouvertes) {
    return (
      <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-3 text-sm">
        <div className="text-green-800 dark:text-green-200 font-medium">
          ✓ Réclamations ouvertes
        </div>
        <div className="text-xs text-green-700 dark:text-green-300 mt-1">
          {fenetre.jours_restants} jour{(fenetre.jours_restants ?? 0) > 1 ? 's' : ''} restant{(fenetre.jours_restants ?? 0) > 1 ? 's' : ''}.
          Limite : {new Date(fenetre.date_limite || '').toLocaleString('fr-FR')}
        </div>
      </div>
    );
  }
  return (
    <div className="bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-3 text-sm">
      <div className="text-gray-800 dark:text-gray-200 font-medium">
        🔒 Réclamations fermées
      </div>
      <div className="text-xs text-gray-600 dark:text-gray-400 mt-1">
        {fenetre.raison === 'expire'
          ? `Le délai a expiré le ${new Date(fenetre.date_limite || '').toLocaleString('fr-FR')}.`
          : 'Les réclamations seront ouvertes après publication des résultats.'}
      </div>
    </div>
  );
};

const ConversationPanel: React.FC<{
  reclamation: ReclamationItem;
  onClose: () => void;
  onUpdate: () => void;
}> = ({ reclamation, onClose, onUpdate }) => {
  const [messages, setMessages] = useState<MessageReclamationItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [contenu, setContenu] = useState('');
  const [sending, setSending] = useState(false);
  const [isCloturee, setIsCloturee] = useState(reclamation.statut === 'cloturee');
  const [error, setError] = useState<string | null>(null);
  const scrollRef = useRef<HTMLDivElement | null>(null);

  const charger = () => {
    setLoading(true);
    portailApi.getReclamationMessages(reclamation.id)
      .then((res) => {
        setMessages(res.results);
        setIsCloturee(res.reclamation.cloturee);
        setTimeout(() => scrollRef.current?.scrollTo({ top: 9e9, behavior: 'smooth' }), 100);
      })
      .catch((e: any) => setError(e.message || 'Erreur de chargement'))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    charger();
    onUpdate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reclamation.id]);

  const envoyer = async () => {
    if (contenu.trim().length < 2) return;
    setSending(true);
    setError(null);
    try {
      const res = await portailApi.sendReclamationMessage(reclamation.id, contenu.trim());
      setMessages(res.results);
      setContenu('');
      setTimeout(() => scrollRef.current?.scrollTo({ top: 9e9, behavior: 'smooth' }), 100);
      onUpdate();
    } catch (e: any) {
      setError(e?.data?.detail || e.message || 'Erreur');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-end sm:items-center justify-center sm:p-4">
      <div className="bg-gray-900 rounded-t-2xl sm:rounded-2xl shadow-2xl max-w-md w-full max-h-[92vh] flex flex-col">
        <div className="px-4 py-3 border-b border-gray-800 flex items-center gap-2">
          <button onClick={onClose} className="text-gray-400 hover:text-gray-200 p-1">←</button>
          <div className="min-w-0 flex-1">
            <div className="font-semibold text-gray-100 text-sm truncate">
              {reclamation.matiere_nom || 'Réclamation'}
            </div>
            <div className="text-xs text-gray-400">
              {TYPE_LABEL[reclamation.type_reclamation]} · {STATUT_INFO[reclamation.statut]?.label}
            </div>
          </div>
          <span className={`text-xs px-2 py-0.5 rounded-full ${STATUT_INFO[reclamation.statut]?.cls}`}>
            {STATUT_INFO[reclamation.statut]?.icon}
          </span>
        </div>

        <div ref={scrollRef} className="flex-1 overflow-y-auto p-3 space-y-2 bg-gray-950">
          {loading ? (
            <div className="text-center text-gray-400 text-sm py-8">Chargement...</div>
          ) : messages.length === 0 ? (
            <div className="text-center text-gray-400 text-sm py-8">Aucun message</div>
          ) : (
            messages.map((m) => {
              const isMoi = m.expediteur === 'etudiant';
              const isSys = m.expediteur === 'systeme';
              return (
                <div key={m.id} className={`flex ${isMoi ? 'justify-end' : 'justify-start'}`}>
                  <div className={`max-w-[80%] rounded-lg px-3 py-2 text-sm ${
                    isSys
                      ? 'bg-yellow-900/30 text-yellow-200 border border-yellow-800/50 italic text-xs mx-auto text-center'
                      : isMoi
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-800 text-gray-100 border border-gray-700'
                  }`}>
                    {!isSys && (
                      <div className={`text-[10px] font-semibold mb-0.5 ${isMoi ? 'text-blue-100' : 'text-blue-300'}`}>
                        {isMoi ? 'Vous' : (m.expediteur_nom || 'Scolarité')}
                      </div>
                    )}
                    <div className="whitespace-pre-wrap break-words">{m.contenu}</div>
                    <div className={`text-[10px] mt-1 opacity-75 ${isMoi ? 'text-blue-100' : 'text-gray-400'}`}>
                      {fmtTime(m.date_creation)}
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>

        <div className="border-t border-gray-800 p-3">
          {error && (
            <div className="mb-2 text-xs text-red-300 bg-red-900/30 border border-red-800 rounded p-2">{error}</div>
          )}
          {isCloturee ? (
            <div className="text-center text-sm text-gray-400 py-2 bg-gray-800/40 rounded">
              🔒 Conversation clôturée par la scolarité
            </div>
          ) : (
            <div className="flex gap-2">
              <input
                type="text"
                value={contenu}
                onChange={(e) => setContenu(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && envoyer()}
                placeholder="Tapez votre message..."
                maxLength={2000}
                className="flex-1 px-3 py-2 text-sm bg-gray-800 border border-gray-700 rounded text-gray-100 placeholder-gray-500"
              />
              <button
                onClick={envoyer}
                disabled={sending || contenu.trim().length < 2}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white text-sm rounded"
              >
                {sending ? '…' : '➤'}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default function MesReclamations() {
  const navigate = useNavigate();
  const [items, setItems] = useState<ReclamationItem[]>([]);
  const [fenetre, setFenetre] = useState<FenetreReclamation | null>(null);
  const [loading, setLoading] = useState(true);
  const [chatRec, setChatRec] = useState<ReclamationItem | null>(null);

  const charger = () => {
    setLoading(true);
    portailApi.mesReclamations()
      .then((res) => {
        setItems(res.results);
        setFenetre(res.fenetre);
      })
      .catch((e: any) => {
        if (e.status === 401) {
          portailApi.clearToken();
          navigate('/portail/login', { replace: true });
        }
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    charger();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <PortailLayout showHeader title="Mes réclamations">
      {chatRec && (
        <ConversationPanel
          reclamation={chatRec}
          onClose={() => { setChatRec(null); charger(); }}
          onUpdate={charger}
        />
      )}

      <div className="space-y-3 mt-2">
        {fenetre && <Fenetre fenetre={fenetre} />}

        {loading ? (
          <div className="text-center py-12 text-gray-400">Chargement...</div>
        ) : items.length === 0 ? (
          <div className="bg-white dark:bg-gray-800 rounded-2xl p-8 text-center border border-gray-200 dark:border-gray-700">
            <div className="text-5xl mb-3 opacity-40">📋</div>
            <p className="text-gray-600 dark:text-gray-300 font-medium">Aucune réclamation déposée</p>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
              Vous pouvez déposer une réclamation depuis votre relevé de notes en cliquant sur le bouton ⚠️ à côté d'une matière.
            </p>
            <button
              onClick={() => navigate('/portail/releve')}
              className="mt-4 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg"
            >
              Voir mes résultats
            </button>
          </div>
        ) : (
          items.map((r) => {
            const info = STATUT_INFO[r.statut] || STATUT_INFO.deposee;
            const nbNonLus = r.nb_non_lus || 0;
            return (
              <div
                key={r.id}
                className={`bg-white dark:bg-gray-800 rounded-2xl shadow-sm border overflow-hidden ${
                  nbNonLus > 0 ? 'border-blue-400 dark:border-blue-600 ring-1 ring-blue-300' : 'border-gray-200 dark:border-gray-700'
                }`}
              >
                <div className="p-4">
                  <div className="flex items-start justify-between mb-2 gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                        {TYPE_LABEL[r.type_reclamation]} {r.semestre && `• ${r.semestre}`}
                      </div>
                      <div className="font-semibold text-gray-900 dark:text-white text-sm mt-0.5">
                        {r.matiere_nom || '—'}
                      </div>
                    </div>
                    <div className="flex flex-col items-end gap-1">
                      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${info.cls}`}>
                        {info.icon} {info.label}
                      </span>
                      {nbNonLus > 0 && (
                        <span className="text-xs px-1.5 py-0.5 rounded-full bg-red-500 text-white font-bold animate-pulse">
                          {nbNonLus} nouveau{nbNonLus > 1 ? 'x' : ''}
                        </span>
                      )}
                    </div>
                  </div>

                  {(r.note_actuelle !== null || r.note_attendue !== null) && (
                    <div className="grid grid-cols-2 gap-2 my-2 text-xs">
                      {r.note_actuelle !== null && (
                        <div className="bg-gray-50 dark:bg-gray-900/30 rounded px-2 py-1.5">
                          <div className="text-gray-400">Note actuelle</div>
                          <div className="font-bold text-gray-900 dark:text-white">{r.note_actuelle}</div>
                        </div>
                      )}
                      {r.note_attendue !== null && (
                        <div className="bg-gray-50 dark:bg-gray-900/30 rounded px-2 py-1.5">
                          <div className="text-gray-400">Note attendue</div>
                          <div className="font-bold text-gray-900 dark:text-white">{r.note_attendue}</div>
                        </div>
                      )}
                    </div>
                  )}

                  {r.reponse_scolarite && (
                    <div className={`mt-3 p-3 rounded-lg border-l-4 ${
                      r.statut === 'acceptee'
                        ? 'border-green-500 bg-green-900/20'
                        : r.statut === 'rejetee'
                          ? 'border-red-500 bg-red-900/20'
                          : 'border-blue-500 bg-blue-900/20'
                    }`}>
                      <div className="text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
                        Dernière réponse de la scolarité
                      </div>
                      <div className="text-sm text-gray-800 dark:text-gray-200 line-clamp-2">
                        {r.reponse_scolarite}
                      </div>
                    </div>
                  )}

                  <div className="flex items-center justify-between mt-3">
                    <div className="text-xs text-gray-400">
                      Déposée le {fmtDate(r.date_creation)}
                    </div>
                    <button
                      onClick={() => setChatRec(r)}
                      className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 hover:bg-blue-100 text-sm font-medium"
                    >
                      💬 Conversation ({r.nb_messages || 0})
                    </button>
                  </div>
                </div>
              </div>
            );
          })
        )}

        <button
          onClick={() => navigate('/portail/accueil')}
          className="w-full py-3 bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-200 rounded-lg text-sm font-medium"
        >
          ← Retour à l'accueil
        </button>
      </div>
    </PortailLayout>
  );
}
